/**
 * Page header of an opened permit procedure – the same on its three pages Antrag, Nachweise and
 * Kosten, which are its tabs.
 *
 * The procedure is chosen here and not in the sidebar: ⌄ next to the title lists the procedures
 * of the project. The meta line carries the status in short form, so it stays visible when the
 * header is collapsed. The stage is the status bar with the next step on the line
 * (`ProcedureStage`).
 */
import React, { useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check as CheckIcon,
  KeyboardArrowDown as SwitchIcon,
} from "@mui/icons-material";
import PageHeader from "../../../layout/PageHeader";
import ProcedureStage from "./ProcedureStage";
import { useProcedureActions } from "./useProcedureActions";
import { colors } from "../../../../styles/theme";
import { PermitProcedure, ProcedureTransition } from "../../../../types";

export type ProcedurePage =
  | "project-permit-elia"
  | "project-permit-evidences"
  | "project-permit-costs";

const TABS: { key: ProcedurePage; label: string }[] = [
  { key: "project-permit-elia", label: "Antrag" },
  { key: "project-permit-evidences", label: "Nachweise" },
  { key: "project-permit-costs", label: "Kosten" },
];

interface ProcedurePageHeaderProps {
  procedure: PermitProcedure;
  /** All procedures of the project, for the switcher */
  procedures: PermitProcedure[];
  /** Short titles of the procedures (`antragKurztitel`) */
  shortTitles: Map<string, string>;
  activePage: ProcedurePage;
  /** Opens a page of a procedure, or without ID the list of all procedures */
  onOpen: (procedureId: string | null, page?: ProcedurePage) => void;
  onTransition: (
    procedureId: string,
    data: ProcedureTransition,
  ) => Promise<PermitProcedure | null>;
  editable: boolean;
  /** Which tabs to show at all – a page the user may not see has no tab */
  isVisible: (page: ProcedurePage) => boolean;
  hasWarning: (page: ProcedurePage) => boolean;
  below?: React.ReactNode;
}

const ProcedurePageHeader: React.FC<ProcedurePageHeaderProps> = ({
  procedure,
  procedures,
  shortTitles,
  activePage,
  onOpen,
  onTransition,
  editable,
  isVisible,
  hasWarning,
  below,
}) => {
  const [switcherAnchor, setSwitcherAnchor] = useState<HTMLElement | null>(
    null,
  );
  const actions = useProcedureActions(onTransition);

  const shortTitle = shortTitles.get(procedure.id) ?? "";
  const title = procedure.bezeichnung || shortTitle;
  const position = procedures.findIndex((p) => p.id === procedure.id) + 1;

  const meta = [
    procedure.withdrawn
      ? "Zurückgenommen"
      : `${procedure.phase_label}: ${procedure.status_label}`,
    procedure.bezeichnung ? shortTitle : null,
    procedure.aktenzeichen ? `Az. ${procedure.aktenzeichen}` : null,
    procedures.length > 1
      ? `${position} von ${procedures.length} Anträgen`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const switcher = (
    <>
      <Tooltip title="Antrag wechseln">
        <IconButton
          aria-label="Antrag wechseln"
          size="small"
          onClick={(event) => setSwitcherAnchor(event.currentTarget)}
          sx={{ color: colors.pageHeader.textSecondary }}
        >
          <SwitchIcon />
        </IconButton>
      </Tooltip>
      <Menu
        open={!!switcherAnchor}
        anchorEl={switcherAnchor}
        onClose={() => setSwitcherAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 280 } } }}
      >
        {procedures.map((p) => (
          <MenuItem
            key={p.id}
            selected={p.id === procedure.id}
            onClick={() => {
              setSwitcherAnchor(null);
              if (p.id !== procedure.id) onOpen(p.id, activePage);
            }}
          >
            <ListItemIcon>
              {p.id === procedure.id && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {p.bezeichnung || shortTitles.get(p.id)}
              </Typography>
              <Typography
                variant="caption"
                noWrap
                sx={{ display: "block", color: colors.text.secondary }}
              >
                {p.bezeichnung ? `${shortTitles.get(p.id)} · ` : ""}
                {p.withdrawn ? "Zurückgenommen" : p.status_label}
              </Typography>
            </Box>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            setSwitcherAnchor(null);
            onOpen(null);
          }}
        >
          <ListItemIcon />
          Alle Anträge
        </MenuItem>
      </Menu>
    </>
  );

  return (
    <>
      <PageHeader
        title={title}
        titleAddon={switcher}
        meta={meta}
        stage={
          <ProcedureStage
            procedure={procedure}
            editable={editable}
            onAction={(action) => actions.openAction(procedure, action)}
            onOpenMenu={(anchor) => actions.openMenu(procedure, anchor)}
          />
        }
        tabs={TABS.filter((tab) => isVisible(tab.key)).map((tab) => ({
          key: tab.key,
          label: tab.label,
          active: tab.key === activePage,
          onSelect: () => onOpen(procedure.id, tab.key),
          warning: hasWarning(tab.key),
        }))}
        below={below}
      />
      {actions.elements}
    </>
  );
};

export default ProcedurePageHeader;
